import type { Dests, Key, Role } from '@lichess-org/chessground/types';

/**
 * Pure UCI <-> Chessground key/destination conversions. No legality decisions
 * live here: the server is authoritative and only supplies legal UCI moves
 * (§6.2). These helpers are kept free of Chessground's DOM machinery so they can
 * be unit-tested without a board.
 */

export type UciMove = string;

const UCI_RE = /^[a-h][1-8][a-h][1-8][qrbn]?$/;

/** Promotion role (Chessground) -> UCI promotion letter. */
export const PROMOTION_LETTERS: Readonly<Record<Role, string>> = {
  queen: 'q',
  rook: 'r',
  bishop: 'b',
  knight: 'n',
  // Not promotion pieces, but kept total so Record<Role, …> is exhaustive.
  pawn: 'p',
  king: 'k',
};

/** The four roles a pawn can promote to. */
export const PROMOTION_ROLES: readonly Role[] = ['queen', 'rook', 'bishop', 'knight'];

export function isUciMove(value: string): value is UciMove {
  return UCI_RE.test(value);
}

/** True when a UCI move carries a promotion suffix (e.g. `e7e8q`). */
export function isPromotionUci(uci: UciMove): boolean {
  return uci.length === 5 && /[qrbn]$/.test(uci);
}

/**
 * Convert legal UCI moves into Chessground's `Dests` map. Promotion moves
 * collapse onto the single destination square (e2e8q/e2e8r/… all share e7→e8).
 */
export function uciMovesToDests(moves: UciMove[]): Dests {
  const dests: Dests = new Map<Key, Key[]>();
  for (const move of moves) {
    if (!isUciMove(move)) continue;
    const orig = move.slice(0, 2) as Key;
    const dest = move.slice(2, 4) as Key;
    const list = dests.get(orig);
    if (list) {
      if (!list.includes(dest)) list.push(dest);
    } else {
      dests.set(orig, [dest]);
    }
  }
  return dests;
}

/** All UCI candidates matching a (orig, dest) pair. */
export function candidatesFor(moves: UciMove[], orig: Key, dest: Key): UciMove[] {
  const prefix = `${orig}${dest}`;
  return moves.filter((move) => move.startsWith(prefix));
}

/**
 * True when the (orig, dest) pair requires a promotion choice — i.e. the legal
 * move set carries more than one promotion suffix for that square.
 */
export function requiresPromotion(moves: UciMove[], orig: Key, dest: Key): boolean {
  return candidatesFor(moves, orig, dest).length > 1;
}

/**
 * Resolve a promotion choice to a concrete UCI move. Returns the 5-char move
 * with the chosen promotion letter, or the plain 4-char move for non-promotion.
 */
export function resolveUci(
  moves: UciMove[],
  orig: Key,
  dest: Key,
  role: Role | null,
): UciMove | null {
  const candidates = candidatesFor(moves, orig, dest);
  if (candidates.length === 0) return null;
  if (role === null) {
    // No promotion role: only valid for the plain (non-promotion) move.
    return candidates.find((move) => move.length === 4) ?? null;
  }
  const letter = PROMOTION_LETTERS[role];
  return candidates.find((move) => move.length === 5 && move[4] === letter) ?? null;
}

/** The (orig, dest) key pair of a UCI move (drops the promotion suffix). */
export function uciToKeyPair(uci: UciMove): [Key, Key] | null {
  if (!isUciMove(uci)) return null;
  return [uci.slice(0, 2) as Key, uci.slice(2, 4) as Key];
}
